/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Card,
  Modal,
  Input,
  message,
} from 'antd';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import Select from 'components/Select';
import { onError } from 'utils/handlers';
import {
  deleteCatalogoDeTarifa,
  getCatalogoDeTarifas,
  patchCatalogoDeTarifa,
  postCatalogoDeTarifa,
} from 'api/configuracion/catalogoDeTarifas';
import { getTiposDeTarifa } from 'api/configuracion';
import { getModelPermission, hasActionPermission, ActionsNames } from 'utils/permission/index';
import Tarifas, { model as tarifaModel } from './Tarifas';

const baseURI = 'configuracion/catalogos-de-tarifas/';

// eslint-disable-next-line react/prop-types
const CatalogoDeTarifas = ({ permission }) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const estadosGlobales = useSelector(({ catalogs }) => catalogs.estadosGlobales);
  const [tipoTarifa, setTipoTarifa] = useState([]);
  const [hasAllViewPermission, setHasAllViewPermission] = useState(true);
  const [permissionTarifa, setPermissionTarifa] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    const _data = await getCatalogoDeTarifas();
    setData(_data);
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const validatePermission = () => {
      const tarifas = getModelPermission(tarifaModel);
      const _hasAllViewPermission = hasActionPermission('tipodetarifa', ActionsNames.view)
        && tarifas.view;
      if (!_hasAllViewPermission) {
        message.warn('Hacen falta permisos en los catálogos relacionados a este módulo');
      }
      if (mounted) {
        setPermissionTarifa(tarifas);
        setHasAllViewPermission(_hasAllViewPermission);
      }
    };
    const fetchAll = async () => {
      setLoading(true);
      const dataTiposDeTarifa = await getTiposDeTarifa();
      if (mounted) setTipoTarifa(dataTiposDeTarifa);
      await fetchData();
      setLoading(false);
    };

    fetchAll();
    validatePermission();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    form.resetFields();
    setSelectedRowKeys([]);
    setVisible(false);
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      const saved = await (values?.id
        ? patchCatalogoDeTarifa(values.id, values) : postCatalogoDeTarifa(values));
      if (saved) {
        onCancel();
        await fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const rules = { required: [{ required: true }] };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickEdit = () => {
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const deleteItem = async () => {
    setLoading(true);
    if (selectedRowKeys.length) {
      const [key] = selectedRowKeys;
      const deleted = await deleteCatalogoDeTarifa(key);
      if (deleted) {
        onCancel();
        setVisibleAlert(false);
        await fetchData();
      }
    }
    setLoading(false);
  };

  const columns = [
    {
      titleText: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      width: 250,
    },
    {
      titleText: 'Tipo de Tarifa',
      dataIndex: 'tipo_de_tarifa',
      key: 'tipo_de_tarifa',
      width: 200,
      render: (val) => (tipoTarifa.find((e) => e.id === val)?.descripcion),
    },
    {
      titleText: 'Estado',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 200,
      render: (val) => (estadosGlobales.find((e) => e.id === val)?.descripcion),
    },
  ];

  // eslint-disable-next-line react/prop-types
  const Formulario = ({ modal = false }) => {
    let props;
    if (modal) {
      props = {
        span: 24,
      };
    } else {
      props = {
        xs: 24,
        sm: 24,
        md: 8,
      };
    }
    return (
      <Form
        layout="vertical"
        form={form}
        name={`catalogo_tarifas${modal ? '_modal' : ''}`}
        onFinish={() => onFinish()}
        scrollToFirstError
        initialValues={{ estados_globales: 1 }}
      >
        <Row gutter={10}>
          <Col {...props}>
            <Form.Item
              name="descripcion"
              rules={rules.required}
              label="Descripción"
              hasFeedback
            >
              <Input allowClear />
            </Form.Item>
          </Col>

          <Col {...props}>
            <Form.Item
              name="tipo_de_tarifa"
              rules={rules.required}
              label="Tipo de tarifa"
              hasFeedback
            >
              <Select
                dataSource={tipoTarifa}
                disabled={!!selectedRowKeys.length}
              />
            </Form.Item>
          </Col>

          <Col {...props}>
            <Form.Item
              name="estados_globales"
              label="Estado"
              hasFeedback
              rules={rules.required}
            >
              <Select
                dataSource={estadosGlobales}
                disabled={!selectedRowKeys.length}
              />
            </Form.Item>
          </Col>

          {(!modal && permissionTarifa.view) && (
            <Tarifas
              tarifa={selectedRowKeys.shift()}
              tipoDeTarifa={form.getFieldValue('tipo_de_tarifa')}
              permission={permission}
            />
          )}
          <Form.Item hidden>
            <Button htmlType="submit" />
          </Form.Item>
        </Row>
      </Form>
    );
  };

  return (
    <Row id="sort-rows" align="center" justify="center" className="container">
      <Spin tip="Cargando..." spinning={loading}>
        {(
          (!visible && !selectedRowKeys.length)
          || (!visible && selectedRowKeys.length)
          || (visible && !selectedRowKeys.length)) && (
            <Table
              cols={columns}
              data={data}
              rowSelection={rowSelection}
              handleOnRowClick={handleOnRowClick}
              controls={{
                onClickAdd,
                onClickEdit,
                onClickDelete,
              }}
              allowImport
              baseURI={baseURI}
              rowKey="id"
              permission={permission}
              hasAllViewPermission={hasAllViewPermission}
            />
        )}
        {(visible && !!selectedRowKeys.length) && (
          <Card
            className="form-container"
            title="Editar Catálogo de Tarifa"
            extra={(
              <FormSubmitControls
                onFinish={onFinish}
                onCancel={onCancel}
                permission={permission}
                loading={loading}
              />
            )}
            bordered={false}
          >
            <Formulario />
          </Card>
        )}
      </Spin>
      <Modal
        visible={visible && !selectedRowKeys.length}
        title={(
          <FormSubmitControls
            label="Agregar Tarifa"
            onFinish={() => onFinish()}
            onCancel={onCancel}
            loading={loading}
            permission={permission}
          />
        )}
        closable={false}
        onCancel={onCancel}
        footer={null}
        keyboard={!loading}
        maskClosable={!loading}
        forceRender
      >
        <Formulario modal />
      </Modal>
      <ModalDelete
        onDelete={deleteItem}
        onCancel={() => setVisibleAlert(false)}
        visible={visibleAlert}
        content={`Tarifa ${form.getFieldValue('descripcion')}`}
        loading={loading}
      />
    </Row>
  );
};

export default CatalogoDeTarifas;
